import type { PageTitle, PlausibleListRow, PlausibleMetricId } from "@/declarations/models/Plausible";
import type { TFunction } from "i18next";

export interface MetricDef {
    id: PlausibleMetricId;
    label: string;
    format?: (raw: unknown) => string;
}

export interface ListConfig {
    dimensions: string;
    mapRow: (row: any, ctx: { t: TFunction; resolveLocalized: (s: any) => string | null | undefined }) => any;
    defaultFilters?: unknown[];
}

export interface StatsTab {
    value: string;
    title: string;
    notFoundText: string;
    loadingText: string;
    loading: boolean;
    resultItem: any;
    selectedItem: PlausibleListRow | null;
    onSelectItem: (item: PlausibleListRow) => void;
    onHandlePageChange: (_: any, newPage: number) => void;
    currentPage: number;
    pageCount: number;
    hideOpenPage?: boolean;
    hidePagePath?: boolean;
    hidden?: boolean;
}

export const METRICS = (t: (key: string) => string): Record<PlausibleMetricId, MetricDef> => ({
    visitors: { id: "visitors", label: t("stats.metrics.visitors") },
    visits: { id: "visits", label: t("stats.metrics.visits") },
    pageviews: { id: "pageviews", label: t("stats.metrics.pageviews") },
    views_per_visit: {
        id: "views_per_visit", label: t("stats.metrics.views_per_visit"),
        format: (v) => Number(v).toFixed(2)
    },
    bounce_rate: {
        id: "bounce_rate", label: t("stats.metrics.bounce_rate"),
        format: (v) => `${v}%`
    },
    visit_duration: {
        id: "visit_duration", label: t("stats.metrics.visit_duration"),
        format: (v) => {
            const seconds = Math.round(Number(v));
            if (seconds < 60) return `${seconds}s`;
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes}m ${remainingSeconds}s`;
        }
    },
});

export function pickMetrics(
    dateRangeId: string,
    hasSelectedPage: boolean,
    hasSelectedQr: boolean,
): PlausibleMetricId[] {
    if (hasSelectedPage || hasSelectedQr) {
        return ["visitors", "visits", "pageviews", "bounce_rate"];
    }
    switch (dateRangeId) {
        case "realTime":
            return ["visitors", "visits", "pageviews"];
        default:
            return ["visitors", "visits", "pageviews",
                "views_per_visit", "bounce_rate", "visit_duration"];
    }
}

export type DateRangeValue =
    | "today" | "yesterday" | "24h" | "7d" | "28d" | "91d"
    | "month" | "lastMonth" | "6mo" | "12mo" | "year" | "lastYear" | "all";

export type ListKind = "pages" | "qrcodes" | "languages";

export interface DateRangeOption {
    value: DateRangeValue;
    label: string;
    plausibleRange: string | [string, string];
}

export const SKIP_RATE_BY_RANGE: Record<DateRangeValue, number> = {
    today: 2,
    yesterday: 2,
    "24h": 4,
    "28d": 5,
    month: 5,
    "91d": 10,
    "6mo": 30,
    "12mo": 30,
    year: 30,
    lastYear: 30,
    all: 30,
    "7d": 1,
    lastMonth: 5,
};

export const DEFAULT_RANGE_VALUE: DateRangeValue = "7d";

export const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function buildDateRangeOptions(t: (key: string) => string): { options: DateRangeOption[]; defaultOption: DateRangeOption; } {
    const now = new Date();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
    const lastYearEnd = new Date(now.getFullYear() - 1, 11, 31);

    const options: DateRangeOption[] = [
        { value: "today", label: t("stats.range.today"), plausibleRange: "day" },
        { value: "yesterday", label: t("stats.range.yesterday"), plausibleRange: [iso(yesterday), iso(yesterday)] },
        { value: "24h", label: t("stats.range.24h"), plausibleRange: "24h" },
        { value: "7d", label: t("stats.range.7d"), plausibleRange: "7d" },
        { value: "28d", label: t("stats.range.28d"), plausibleRange: "28d" },
        { value: "91d", label: t("stats.range.91d"), plausibleRange: "91d" },
        { value: "month", label: t("stats.range.month"), plausibleRange: "month" },
        { value: "lastMonth", label: t("stats.range.lastMonth"), plausibleRange: [iso(firstOfLastMonth), iso(lastOfLastMonth)] },
        { value: "6mo", label: t("stats.range.6mo"), plausibleRange: "6mo" },
        { value: "12mo", label: t("stats.range.12mo"), plausibleRange: "12mo" },
        { value: "year", label: t("stats.range.year"), plausibleRange: "year" },
        { value: "lastYear", label: t("stats.range.lastYear"), plausibleRange: [iso(lastYearStart), iso(lastYearEnd)] },
        { value: "all", label: t("stats.range.all"), plausibleRange: "all" },
    ];
    const defaultOption = options.find(o => o.value === DEFAULT_RANGE_VALUE);
    if (!defaultOption) {
        throw new Error(`Missing default date range: ${DEFAULT_RANGE_VALUE}`);
    }
    return { options, defaultOption };
}

export type DetailsFormatters = {
    time: Intl.DateTimeFormat;
    dayMonthTime: Intl.DateTimeFormat;
    dayMonthYear: Intl.DateTimeFormat;
};

export function buildDetailsFormatters(locale: string | string[]): DetailsFormatters {
    return {
        time: new Intl.DateTimeFormat(locale, {
            hour: "2-digit",
            minute: "2-digit",
        }),
        dayMonthTime: new Intl.DateTimeFormat(locale, {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        }),
        dayMonthYear: new Intl.DateTimeFormat(locale, {
            day: "numeric",
            month: "short",
            year: "numeric",
        }),
    };
}

export function pickDetailsLabel(
    dateRangeValue: DateRangeValue,
    label: string,
    formatters: DetailsFormatters,
): string {
    const d = new Date(label);
    switch (dateRangeValue) {
        case "today":
        case "yesterday":
            return formatters.time.format(d);
        case "24h":
            return formatters.dayMonthTime.format(d);
        default:
            return formatters.dayMonthYear.format(d);
    }
}

export function buildListConfigs(
    t: (k: string) => string,
    resolveLocalized: (s: any) => string | null | undefined,
): Record<ListKind, ListConfig> {
    return {
        pages: {
            dimensions: "event:page",
            defaultFilters: [["has_not_done", ["is", "event:goal", ["A QR code is scanned"]]]],
            mapRow: (page, { resolveLocalized }) => ({
                ...page,
                displayTitle: resolveTitles(page.titles, resolveLocalized),
            }),
        },
        qrcodes: {
            dimensions: "event:page",
            defaultFilters: [["is", "event:goal", ["A QR code is scanned"]]],
            mapRow: (page, { resolveLocalized }) => ({
                ...page,
                displayTitle: resolveTitles(page.titles, resolveLocalized),
            }),
        },
        languages: {
            dimensions: "event:props:language",
            defaultFilters: [["is_not", "event:props:language", ["(none)"]]],
            mapRow: (page, { t }) => {
                const code = page.dimensions[0];
                return {
                    ...page,
                    displayTitle:
                        code === "(none)"
                            ? "N/A"
                            : t(`languages.${code === "nb" ? "no" : code}`) +
                            (code === "nb" ? " (Bokmål)" : ""),
                };
            },
        },
    };
}

function resolveTitles(titles: PageTitle[] = [], resolveLocalized: (s: any) => string | null | undefined) {
    const resolved = titles
        .map((e) => (e?.internal_title ? e.internal_title : resolveLocalized(e.locale_title)))
        .filter(Boolean) as string[];
    return resolved.length ? resolved.join(" – ") : null;
}
